import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { connect } from "react-redux";

import MainLayout from "../Common/MainLayout";
import api from "../../Services/Api/api";
import apiPresets from "../../Services/Api/api-presets";
import GeoTaggedImage from "../../Components/Media/GeoTaggedImage";

const VisitDetailsScreen = ({ user }) => {
  const { visitId } = useParams();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVisitDetails();
  }, []);

  const fetchVisitDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch updated visit details
      const visits = await apiPresets.getVisits({
        where: { _id: visitId },
        populate: [
          {
            path: "office",
            populate: [
              {
                path: "subdivision block deptBlock circle lac",
              },
              { path: "type", populate: "category" },
            ],
          },
          { path: "reports", populate: { path: "answers.question" } },
        ],
      });

      console.log({ visits });
      if (visits?.length === 0) throw new Error("Visit not found");

      const visit = visits[0];
      const report = visit.reports?.find((x) => x.status !== "pending");
      setVisit({ ...visit, report });

      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  if (loading)
    return (
      <MainLayout screenName="visits">
        <p>Loading...</p>
      </MainLayout>
    );
  if (error)
    return (
      <MainLayout screenName="visits">
        <p>{error}</p>
      </MainLayout>
    );

  return (
    <MainLayout screenName="visits">
      <div className="visit-details-container">
        <div className="details-header">
          <h2 className="details-title">Visit Details</h2>
        </div>

        {/* Two Column Layout */}
        <div className="details-two-column">
          {/* Left Column - Visitor Details */}
          <div className="details-column">
            <div className="details-section">
              <h3>Visitor Details</h3>
              <div className="visitor-info">
                {visit.visitor?.image ? (
                  <img
                    src={api.getFileLink(visit.visitor?.image)}
                    alt="Visitor"
                    className="profile-photo"
                  />
                ) : (
                  <div className="profile-photo ">
                    <span>👤</span>
                  </div>
                )}
                <div>
                  <strong>{visit.visitor?.name}</strong>
                  <span> {visit.visitor?.designation || ""}</span>
                </div>
              </div>
            </div>

            <div className="details-section">
              <h3>Report Status</h3>
              <div className="detail-row">
                <span className="label">Status</span>
                <span>{visit.report?.status || "Pending"}</span>
              </div>
              {visit?.report && (
                <>
                  {" "}
                  <div className="detail-row">
                    <span className="label">Visited Date</span>
                    <span>
                      {new Date(visit.report?.visitedDate).toLocaleString() ||
                        "-"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Submitted Date</span>
                    <span>
                      {new Date(
                        visit.report?.lastSubmitDate
                      ).toLocaleString() || "-"}
                    </span>
                  </div>
                  {visit.report?.lastReviewedDate && (
                    <div className="detail-row">
                      <span className="label">Reviewed Date</span>
                      <span>
                        {new Date(
                          visit.report?.lastReviewedDate
                        ).toLocaleString() || "-"}
                      </span>
                    </div>
                  )}
                  {visit.report?.lastReviewText && (
                    <div className="detail-row">
                      <span className="label">Review Remark</span>
                      <span>{visit.report.lastReviewText || "-"}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right Column - Office or Scheme Details */}
          <div className="details-column">
            {visit.scheme ? (
              <div className="details-section">
                <h3>Scheme Details</h3>
                <div className="detail-row">
                  <span className="label">Scheme Name</span>
                  <Link to={`/scheme/${visit.scheme._id}`}>
                    <span>{visit.scheme?.name || "-"}</span>
                  </Link>
                </div>
                <div className="detail-row">
                  <span className="label">Details</span>
                  <span>{visit.scheme?.details || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Office</span>
                  <span>{visit.scheme?.office?.name || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Last Date</span>
                  <span>{visit.scheme?.lastDate || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Amount</span>
                  <span>{visit.scheme?.amount || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Block</span>
                  <span>{visit.scheme?.block?.name || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Sub Division</span>
                  <span>{visit.scheme?.subdivision?.name || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Circle</span>
                  <span>{visit.scheme?.circle?.name || "-"}</span>
                </div>

                <div className="detail-row">
                  <span className="label">LAC</span>
                  <span>{visit.scheme?.lac?.name || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Details</span>
                  <span>{visit.scheme?.details || "-"}</span>
                </div>
              </div>
            ) : visit.office ? (
              <div className="details-section">
                <h3>Office Details</h3>
                <div className="detail-row">
                  <span className="label">Office Name</span>
                  <Link to={`/office/${visit.office._id}`}>
                    <span>{visit.office?.name || "-"}</span>
                  </Link>
                </div>
                <div className="detail-row">
                  <span className="label">Office Type</span>
                  <span>{visit.office?.type?.name || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Office Category</span>
                  <span>{visit.office?.type?.category?.name || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Block</span>
                  <span>{visit.office?.block?.name || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Sub Division</span>
                  <span>{visit.office?.subdivision?.name || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Circle</span>
                  <span>{visit.office?.circle?.name || "-"}</span>
                </div>

                <div className="detail-row">
                  <span className="label">LAC</span>
                  <span>{visit.office?.lac?.name || "-"}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Details</span>
                  <span>{visit.office?.details || "-"}</span>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {visit?.report && (
          <div className="details-section">
            <h3>Visitor's Report</h3>

            {/* Visitor's Remark */}
            <div className="detail-row">
              <span className="label">Remark</span>
              <span>{visit.report?.description || "-"}</span>
            </div>

            <div className="detail-row">
              <span className="label">Rating (out of 10)</span>
              <span>{visit.report?.rating || "-"}</span>
            </div>

            {/* Geo-Tagged Images */}
            {visit.report?.medias?.length > 0 && (
              <div className="detail-row">
                <span className="label">Images</span>
                <div className="photo-grid">
                  {visit.report.medias.map((media, index) => (
                    <GeoTaggedImage
                      key={index}
                      src={api.getFileLink(media.file)}
                      // className="report-photo"
                      coord={media?.coord}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Feedback Questions & Answers */}
            {visit.report?.answers?.length > 0 && (
              <div className="detail-row">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Question</th>
                      <th>Answer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visit.report.answers.map((answer, index) => (
                      <tr key={index}>
                        <td>{answer.question?.question || "-"}</td>
                        <td>{answer.answer || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default VisitDetailsScreen;
